'use client'
import { HelperText } from '@/components/form'
import { Box, TextField } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'

type OTPInputComponentProps = {
  length?: number
  value?: string
  onChange?: (value: string) => void
  onComplete?: (value: string) => void
  disabled?: boolean
  error?: boolean
  helperText?: string
  autoFocus?: boolean
}

export const OTPInput = ({
  length = 6,
  value = '',
  onChange,
  onComplete,
  disabled = false,
  error = false,
  helperText,
  autoFocus = false,
}: OTPInputComponentProps) => {
  const [otpValues, setOtpValues] = useState<string[]>(new Array(length).fill(''))
  const inputRefs = useRef<(HTMLDivElement | null)[]>(new Array(length).fill(null))
  console.log('OTPInput rendered with values:', otpValues)

  // // Sync with external value
  // useEffect(() => {
  //   if (value) {
  //     const newValues = value.split('').slice(0, length)
  //     while (newValues.length < length) {
  //       newValues.push('')
  //     }
  //     setOtpValues(newValues)
  //   } else {
  //     setOtpValues(new Array(length).fill(''))
  //   }
  // }, [value, length])

  // Auto focus vào ô đầu tiên khi component mount và autoFocus enabled
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      setTimeout(() => {
        const firstInput = inputRefs.current[0]
        if (firstInput) {
          const input = firstInput.querySelector('input')
          input?.focus()
        }
      }, 100)
    }
  }, [autoFocus])
  const handleChange = (index: number, newValue: string) => {
    // Debug log để kiểm tra
    console.log('handleChange called:', {
      index,
      newValue,
      currentValue: otpValues[index],
      activeElement: document.activeElement,
    })
    console.log('OTP Values before change:', otpValues)
    // Nếu newValue rỗng (khi xóa), cho phép
    if (newValue === '') {
      const newOtpValues = [...otpValues]
      newOtpValues[index] = ''
      console.log('Updating OTP Values to:', newOtpValues)
      setOtpValues(newOtpValues)
      onChange?.(newOtpValues.join(''))
      return
    }

    // Nếu có giá trị, chỉ lấy ký tự cuối cùng và phải là số
    const lastChar = newValue.slice(-1)
    if (!/^\d$/.test(lastChar)) {
      console.log('Invalid character:', lastChar)
      return
    }
    console.log('Valid character entered:', lastChar)
    const newOtpValues = [...otpValues]
    newOtpValues[index] = lastChar
    console.log('Updating OTP Values to:', newOtpValues)
    setOtpValues(newOtpValues)

    // Call onChange callback
    const otpString = newOtpValues.join('')
    onChange?.(otpString)

    // Auto-focus next input chỉ khi có value và chưa phải ô cuối
    if (lastChar && index < length - 1) {
      setTimeout(() => {
        const nextInput = inputRefs.current[index + 1]
        if (nextInput) {
          const input = nextInput.querySelector('input')
          input?.focus()
        }
      }, 0)
    }

    // Auto-submit when complete
    if (lastChar && newOtpValues.every((val) => val !== '') && newOtpValues.length === length) {
      onComplete?.(otpString)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLDivElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newOtpValues = [...otpValues]

      if (otpValues[index]) {
        // Nếu ô hiện tại có value, xóa nó
        newOtpValues[index] = ''
        setOtpValues(newOtpValues)
        onChange?.(newOtpValues.join(''))
      } else if (index > 0) {
        // Nếu ô hiện tại trống, xóa ô trước đó và focus vào nó
        newOtpValues[index - 1] = ''
        setOtpValues(newOtpValues)
        onChange?.(newOtpValues.join(''))

        setTimeout(() => {
          const prevInput = inputRefs.current[index - 1]
          if (prevInput) {
            const input = prevInput.querySelector('input')
            input?.focus()
          }
        }, 0)
      }
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      setTimeout(() => {
        const prevInput = inputRefs.current[index - 1]
        if (prevInput) {
          const input = prevInput.querySelector('input')
          input?.focus()
        }
      }, 0)
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      setTimeout(() => {
        const nextInput = inputRefs.current[index + 1]
        if (nextInput) {
          const input = nextInput.querySelector('input')
          input?.focus()
        }
      }, 0)
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain')
    const digits = pastedData.replace(/\D/g, '').slice(0, length)

    if (digits) {
      const newOtpValues = new Array(length).fill('')
      for (let i = 0; i < digits.length; i++) {
        newOtpValues[i] = digits[i]
      }
      setOtpValues(newOtpValues)
      onChange?.(newOtpValues.join(''))

      // Focus the next empty input or the last input
      const nextIndex = Math.min(digits.length, length - 1)
      setTimeout(() => {
        const nextInput = inputRefs.current[nextIndex]
        if (nextInput) {
          const input = nextInput.querySelector('input')
          input?.focus()
        }
      }, 0)

      // Auto-submit if complete
      if (digits.length === length) {
        onComplete?.(digits)
      }
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select()
  }

  return (
    <Box>
      <OTPContainer>
        {otpValues.map((digit, index) => (
          <OTPInputStyled
            key={`otp-${index}`}
            ref={(el) => {
              inputRefs.current[index] = el
            }}
            value={digit}
            onChange={(e) => {
              console.log('TextField onChange event:', { index, value: e.target.value, target: e.target })
              handleChange(index, e.target.value)
            }}
            type="number"
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={disabled}
            error={error}
            variant="outlined"
            inputProps={{
              maxLength: 1,
              'aria-label': `OTP digit ${index + 1}`,
              placeholder: '_',
              id: `otp-input-${index}`,
            }}
            sx={{
              '& .MuiOutlinedInput-root input': {
                color: !!digit ? 'text.primary' : 'text.disabled',
                '&::placeholder': {
                  color: 'text.disabled',
                  opacity: 0.7,
                },
              },
            }}
          />
        ))}
      </OTPContainer>
      <div className="mt-1 flex justify-center">
        <HelperText errorMessage={helperText} helperText={helperText} />
      </div>
    </Box>
  )
}

const OTPContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
}))

const OTPInputStyled = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    width: '44px',
    height: '44px',
    fontSize: '18px',
    borderRadius: '8px',
    fontWeight: 'bold',
    textAlign: 'center',
    '& input': {
      textAlign: 'center',
    },
    '&.Mui-focused': {
      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'center',
    // Ẩn nút tăng giảm mặc định của trình duyệt
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&[type=number]': {
      MozAppearance: 'textfield',
    },
  },
}))
